import React, { useState } from 'react';
import { patentApi } from '../../services/api';
import { idsApi } from '../../services/idsApi';
import { copyToClipboard } from '../../utils/clipboard';

// New format marker (TEXT: / PDF: pipe-separated)
const LINKS_MARKER_NEW = '\n\n---\nPRIOR ART LINKS:';
// Legacy format marker (US-only plain URL)
const LINKS_MARKER_OLD = '\n\n---\nPRIOR ART PDF LINKS (US References):';
// OA document identifier marker (app_no/doc_id) — used to fetch PDF via backend
const OA_DOC_MARKER = '\n\n---\nOA DOC: ';

interface PriorArtLink {
  label: string;
  textPath: string | null;   // relative path e.g. /US11941156 or /EP1234567A1
  pdfUrl: string | null;     // absolute URL, US only
}

function parseOAEmail(rawText: string): { emailBody: string; priorArtLinks: PriorArtLink[]; oaDocRef: string | null } {
  // Strip OA doc ref first (may appear at the very end, after prior art links)
  let oaDocRef: string | null = null;
  let text = rawText;
  const oaDocIdx = rawText.indexOf(OA_DOC_MARKER);
  if (oaDocIdx >= 0) {
    const refLine = rawText.slice(oaDocIdx + OA_DOC_MARKER.length).split('\n')[0].trim();
    if (refLine) oaDocRef = refLine;
    text = rawText.slice(0, oaDocIdx);
  }

  // Prefer new format; fall back to legacy
  let markerIdx = text.indexOf(LINKS_MARKER_NEW);
  const isNewFormat = markerIdx >= 0;
  if (!isNewFormat) markerIdx = text.indexOf(LINKS_MARKER_OLD);

  const emailBody = markerIdx >= 0 ? text.slice(0, markerIdx) : text;
  const markerLen = isNewFormat ? LINKS_MARKER_NEW.length : LINKS_MARKER_OLD.length;
  const linksRaw = markerIdx >= 0 ? text.slice(markerIdx + markerLen).trim() : '';

  const priorArtLinks: PriorArtLink[] = [];
  if (!linksRaw) return { emailBody, priorArtLinks, oaDocRef };

  linksRaw.split('\n').forEach(line => {
    const trimmed = line.replace(/^[•\s]+/, '').trim();
    if (!trimmed) return;

    if (isNewFormat) {
      // New format: "Label | TEXT:/US11941156 | PDF:https://..."
      const parts = trimmed.split(' | ');
      const label = parts[0].trim();
      let textPath: string | null = null;
      let pdfUrl: string | null = null;
      parts.slice(1).forEach(part => {
        if (part.startsWith('TEXT:')) textPath = part.slice(5).trim();
        else if (part.startsWith('PDF:')) pdfUrl = part.slice(4).trim();
      });
      if (label && textPath) priorArtLinks.push({ label, textPath, pdfUrl });
    } else {
      // Legacy format: "Label: https://..."
      const sepIdx = trimmed.lastIndexOf(': https://');
      if (sepIdx >= 0) {
        priorArtLinks.push({
          label: trimmed.slice(0, sepIdx).trim(),
          textPath: null,
          pdfUrl: trimmed.slice(sepIdx + 2).trim(),
        });
      }
    }
  });

  return { emailBody, priorArtLinks, oaDocRef };
}

/** Classify prior art textPaths into the IDS download format */
function classifyPriorArtLinks(links: PriorArtLink[]) {
  const usPatents: { patent_number: string }[] = [];
  const usApplications: { publication_number: string }[] = [];
  const foreignPatents: { country: string; docNumber: string; kind?: string }[] = [];

  for (const { textPath } of links) {
    if (!textPath) continue;
    const path = textPath.startsWith('/') ? textPath.slice(1) : textPath;

    if (/^US\d+$/i.test(path)) {
      const num = path.slice(2);
      // US publications are 11 digits (4-digit year + 7-digit seq)
      if (num.length === 11) {
        usApplications.push({ publication_number: num });
      } else {
        usPatents.push({ patent_number: num });
      }
    } else {
      // Foreign: 2-letter country code + digits + optional kind code
      const m = path.match(/^([A-Z]{2})(\d+)([A-Z]\d*)?$/i);
      if (m) {
        foreignPatents.push({
          country: m[1].toUpperCase(),
          docNumber: m[2],
          kind: m[3]?.toUpperCase() || undefined,
        });
      }
    }
  }

  return { usPatents, usApplications, foreignPatents };
}

function buildMailtoUrl(emailBody: string): string {
  const lines = emailBody.split('\n');
  let subject = 'Office Action Report';
  let bodyText = emailBody;

  const firstLine = lines[0]?.trim();
  if (firstLine && (firstLine.toLowerCase().startsWith('subject:') || firstLine.toLowerCase().startsWith('re:'))) {
    subject = firstLine.replace(/^subject:\s*/i, '').replace(/^re:\s*/i, 'Re: ');
    bodyText = lines.slice(1).join('\n').trim();
  }

  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(bodyText)}`;
}

function buildEmailPlainText(emailBody: string, priorArtLinks: PriorArtLink[]): string {
  if (priorArtLinks.length === 0) return emailBody;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const linkRows = priorArtLinks.map(({ label, textPath, pdfUrl }) => {
    const parts: string[] = [];
    if (textPath) parts.push(`Text: ${origin}${textPath}`);
    if (pdfUrl) parts.push(`PDF: ${pdfUrl}`);
    return `• ${label}: ${parts.join(' | ')}`;
  }).join('\n');

  return `${emailBody}\n\nPrior Art Links:\n${linkRows}`;
}

function buildEmailHtml(emailBody: string, priorArtLinks: PriorArtLink[]): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  // Escape HTML special chars and preserve line breaks
  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const bodyHtml = escapeHtml(emailBody).replace(/\n/g, '<br>\n');

  if (priorArtLinks.length === 0) return bodyHtml;

  const linkRows = priorArtLinks.map(({ label, textPath, pdfUrl }) => {
    const parts: string[] = [];
    if (textPath) parts.push(`<a href="${origin}${textPath}">View Text</a>`);
    if (pdfUrl) parts.push(`<a href="${pdfUrl}">View PDF</a>`);
    return `• ${escapeHtml(label)}: ${parts.join(' &nbsp;|&nbsp; ')}`;
  }).join('<br>\n');

  return `${bodyHtml}<br><br><strong>Prior Art Links:</strong><br>\n${linkRows}`;
}

interface OAEmailResultViewProps {
  /** Raw string result from the task (may include the prior art links section) */
  data: unknown;
  showOpenInEmail?: boolean;
}

export function OAEmailResultView({ data, showOpenInEmail = true }: OAEmailResultViewProps) {
  const rawText = typeof data === 'string' ? data : JSON.stringify(data, null, 2);
  const { emailBody, priorArtLinks, oaDocRef } = parseOAEmail(rawText);
  const hasLinks = priorArtLinks.length > 0;

  const [copyLabel, setCopyLabel] = useState('Copy');
  const [oaPdfLabel, setOaPdfLabel] = useState('Download OA PDF');
  const [priorArtDlStatus, setPriorArtDlStatus] = useState<string | null>(null);

  const handleDownloadOaPdf = async () => {
    if (!oaDocRef) return;
    const slashIdx = oaDocRef.indexOf('/');
    if (slashIdx < 0) return;
    const appNo = oaDocRef.slice(0, slashIdx);
    const docId = oaDocRef.slice(slashIdx + 1);
    setOaPdfLabel('Downloading...');
    try {
      const url = await patentApi.downloadOfficeActionPdf(appNo, docId);
      window.open(url, '_blank');
    } catch {
      // silent — API already logs
    } finally {
      setOaPdfLabel('Download OA PDF');
    }
  };

  const handleDownloadPriorArt = async () => {
    const { usPatents, usApplications, foreignPatents } = classifyPriorArtLinks(priorArtLinks);
    const total = usPatents.length + usApplications.length + foreignPatents.length;
    if (total === 0) return;

    // Derive the single-file name to match what the worker would put inside the ZIP
    let singleFileName: string | null = null;
    if (total === 1) {
      if (usPatents.length === 1) singleFileName = `US${usPatents[0].patent_number}.pdf`;
      else if (usApplications.length === 1) singleFileName = `US${usApplications[0].publication_number}.pdf`;
      else if (foreignPatents.length === 1) {
        const fp = foreignPatents[0];
        singleFileName = `${fp.country}${fp.docNumber}${fp.kind ?? ''}.pdf`;
      }
    }

    setPriorArtDlStatus(`Queuing ${total} PDF${total !== 1 ? 's' : ''}...`);
    try {
      const jobId = await idsApi.downloadReferencePdfs({
        idsName: 'prior_art',
        usPatents,
        usApplications,
        foreignPatents,
      });

      setPriorArtDlStatus('Downloading...');
      const result = await idsApi.waitForJobCompletion(jobId);

      if (result.status === 'complete' && (result.idsKey || result.result)) {
        const fileKey = result.idsKey || result.result;
        const isZip = fileKey.endsWith('.zip');
        const filename = isZip ? 'prior_art_references.zip' : (singleFileName ?? 'prior_art_reference.pdf');
        setPriorArtDlStatus('Preparing download...');
        const blob = await idsApi.downloadFile(fileKey);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch {
      // silent
    } finally {
      setPriorArtDlStatus(null);
    }
  };

  const handleCopy = async () => {
    const copied = await copyToClipboard(
      buildEmailPlainText(emailBody, priorArtLinks),
      buildEmailHtml(emailBody, priorArtLinks),
    );
    setCopyLabel(copied ? 'Copied!' : 'Copy failed');
    setTimeout(() => setCopyLabel('Copy'), 2000);
  };

  const handleOpenEmail = () => window.open(buildMailtoUrl(emailBody), '_blank');

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-lg bg-white border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-gray-900">Generated Email Report</h3>
          <div className="flex gap-2">
            <button
              onClick={handleCopy}
              className="text-xs px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              {copyLabel}
            </button>
            {showOpenInEmail && (
              <button
                onClick={handleOpenEmail}
                className="text-xs px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700"
              >
                Open in Email
              </button>
            )}
            {oaDocRef && (
              <button
                onClick={handleDownloadOaPdf}
                className="text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                {oaPdfLabel}
              </button>
            )}
          </div>
        </div>
        <pre className="text-sm font-mono whitespace-pre-wrap text-gray-800 leading-relaxed">
          {emailBody}
        </pre>
        {hasLinks && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-gray-700">Prior Art Links</h4>
              <button
                onClick={handleDownloadPriorArt}
                disabled={!!priorArtDlStatus}
                className="text-xs px-3 py-1 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-60"
              >
                {priorArtDlStatus ?? 'Download All PDFs'}
              </button>
            </div>
            <ul className="space-y-1.5">
              {priorArtLinks.map(({ label, textPath, pdfUrl }) => (
                <li key={textPath ?? pdfUrl ?? label} className="text-sm flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  <span className="text-gray-700 shrink-0">{label}:</span>
                  {textPath && (
                    <a
                      href={textPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View Text
                    </a>
                  )}
                  {pdfUrl && (
                    <a
                      href={pdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View PDF
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}
